import { DateTime } from '@ksyos/date-time';
import { Alert, Badge } from '@ksyos/design-system';
import bind from 'bind-decorator';
import { Component } from 'react';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization } from '@common/enums';
import { ValidationErrorCode } from '@common/enums/validationErrorCode';
import { isDateInBetweenTwoDates } from '@common/utils/dates';
import { tryParseInt } from '@common/utils/tryParseInt';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    Header,
    Link,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { ListPager } from '../../elements/ListPager';
import { FullWidthTemplate } from '../../templates/FullWidthTemplate';

type InsurerListProps = IWithRouterProps;

interface IInsurer {
    authorization: {
        canDelete: boolean;
        canUpdate: boolean;
    };
    id: string;
    isActive: boolean;
    name: string;
    turnoverCeilingEndDate: Date | string | null;
    turnoverCeilingStartDate: Date | string | null;
}

interface IInsurerListState {
    authorization: {
        canAdd: boolean;
    };
    errorMessage?: string;
    isSearching: boolean;
    items: IInsurer[];
    total: number;
}

class InnerInsurersList extends Component<InsurerListProps, IInsurerListState> {
    constructor(props: InsurerListProps, context?: {}) {
        super(props, context);

        assertHasAuthorization(ManagerAuthorization.insurersRead);

        this.state = {
            items: [],
            authorization: {
                canAdd: false,
            },
            isSearching: true,
            total: 0,
        };
    }

    public async componentDidMount() {
        await this.search();
    }

    public render() {
        let actionButtons: JSX.Element[] | undefined;
        const page = this.props.router.searchParams.getOptional('page');
        if (this.state.authorization.canAdd) {
            actionButtons = [
                <LinkButton
                    icon="add"
                    to="/insurers/add"
                >
                    {translateKey('insurers.add')}
                </LinkButton>,
            ];
        }

        return (
            <FullWidthTemplate
                breadcrumb={[
                    { text: translateKey('common.settings') },
                    { text: translateKey('insurers.insurers'), url: '/insurers' },
                ]}
            >
                <Header
                    actionButtons={actionButtons}
                    content={translateKey('insurers.insurers')}
                />

                {this.state.errorMessage && <Alert appearance="warning">{this.state.errorMessage}</Alert>}

                <AdminTable>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('common.name')}</TableHeader>
                            <TableHeader />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.state.items.map((item) => (
                            <TableRow key={item.id}>
                                <TableData>
                                    {item.authorization.canUpdate ? (
                                        <Link to={`/insurers/${item.id}`}>{item.name}</Link>
                                    ) : (
                                        item.name
                                    )}
                                </TableData>
                                {item.authorization.canUpdate && (
                                    <TableData align="right">
                                        {isDateInBetweenTwoDates(
                                            DateTime.now().format('YYYY-MM-DD'),
                                            item.turnoverCeilingStartDate,
                                            item.turnoverCeilingEndDate
                                        ) && (
                                            <Badge
                                                color="orange"
                                                size="small"
                                                style={{ marginRight: '10px' }}
                                                text={translateKey('insurers.contractedCareCannotBeStarted')}
                                            />
                                        )}
                                        <EditButton
                                            to={`/insurers/${item.id}/edit${(page && `?returnPage=${page}`) || ''}`}
                                        />
                                        {item.authorization.canDelete && (
                                            <DeleteButton onClick={() => this.remove(item.id)} />
                                        )}
                                    </TableData>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </AdminTable>
                <ListPager
                    isDisabled={this.state.isSearching}
                    onChangePage={this.handleNavigation}
                    page={tryParseInt(page, 1)}
                    total={this.state.total}
                />
            </FullWidthTemplate>
        );
    }

    @bind
    private async handleNavigation(page: number) {
        this.props.router.navigate(`/insurers?page=${page}`);
        await this.search();
    }

    private async search() {
        const page = this.props.router.searchParams.get('page') ?? undefined;
        await this.setState({ isSearching: true });
        const url = `/api/insurers?includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${page}`;

        const result = await fetchJson(url, 'GET');
        await this.setState({
            items: result.json.items,
            isSearching: false,
            total: result.json.total,
            authorization: result.json.authorization,
        });
    }

    private async remove(id: string) {
        const url = '/api/insurers/' + id;
        await this.setState({ isSearching: true, errorMessage: undefined });
        const result = await fetchJson(url, 'DELETE', { insurerId: id }, [
            ValidationErrorCode.INSURER_HAS_COUPLED_UZOVIES,
            ValidationErrorCode.INSURER_HAS_LINKED_DECLARATION_RULES,
        ]);

        if (result.errorCode === ValidationErrorCode.INSURER_HAS_COUPLED_UZOVIES) {
            await this.setState({ errorMessage: translateKey('insurers.cannotRemoveInsurer') });
        }

        if (result.errorCode === ValidationErrorCode.INSURER_HAS_LINKED_DECLARATION_RULES) {
            await this.setState({ errorMessage: translateKey('insurers.hasLinkedDeclarationRules') });
        }

        await this.search();
    }
}

export const InsurersList = withRouter(InnerInsurersList);
